import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import Papa from "papaparse";
import { exportSchedulePDF } from "./exportSchedulePDF.js";
import ImportValidationModal from "./ImportValidationModal.js";
import { validateImport, parseCSVRowToStudent } from "../utils/importValidation.js";
import "./Export.css";

const API_URL = process.env.REACT_APP_API_URL;

const Export = () => {
  const [students, setStudents] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showValidation, setShowValidation] = useState(false);
  const [parsedStudents, setParsedStudents] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, scheduleRes] = await Promise.all([
        axios.get(`${API_URL}/api/students`),
        axios.get(`${API_URL}/api/schedule`)
      ]);
      setStudents(studentsRes.data);
      setSchedule(scheduleRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Fehler beim Abrufen der Daten:", error);
      toast.error("Fehler beim Abrufen der Daten");
      setLoading(false);
    }
  };

  const handlePDFExport = () => {
    exportSchedulePDF(schedule, students);
  };

  const handleBlankSchedule = async () => {
    if (!window.confirm("⚠️ WARNUNG: Dies löscht ALLE Schüler aus der Datenbank!\n\nNur Trainer bleiben erhalten.\n\nSie können Schüler später aus gespeicherten Plänen wiederherstellen.\n\nFortfahren?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/students/all`);
      toast.success("Alle Schüler wurden gelöscht. Nur Trainer sind noch vorhanden.");
      fetchData(); // Reload data
    } catch (error) {
      console.error("Fehler beim Löschen der Schüler:", error);
      toast.error("Fehler beim Löschen der Schüler");
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCsvFile(file);
      parseCSVPreview(file);
    }
  };

  const parseCSVPreview = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let text = e.target.result;

        // Remove BOM if present
        if (text.charCodeAt(0) === 0xFEFF) {
          text = text.substring(1);
        }

        console.log("Parsing CSV with PapaParse...");

        // Use PapaParse for proper CSV parsing
        const parseResult = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim(),
          dynamicTyping: false, // Keep all values as strings
          encoding: 'UTF-8'
        });

        if (parseResult.errors.length > 0) {
          console.warn('CSV parsing warnings:', parseResult.errors);
        }

        const parsedData = parseResult.data;

        if (parsedData.length === 0) {
          toast.error("CSV-Datei ist leer oder ungültig");
          return;
        }

        const headers = parseResult.meta.fields || [];

        console.log("CSV Preview - Headers:", headers);
        console.log("CSV Preview - First row:", parsedData[0]);
        console.log("CSV Preview - Total rows:", parsedData.length);

        // Helper function to parse available times from day columns
        const parseDayHours = (hoursString, dayName) => {
          if (!hoursString || !hoursString.trim()) return [];
          return hoursString.split(',')
            .map(h => h.trim())
            .filter(h => h && h !== '')
            .map(hour => `${dayName} ${hour}`);
        };

        // Convert PapaParse row objects to student objects
        const parsed = parsedData.map(row => {
          return {
            firstName: row['Vorname'] || '',
            lastName: row['Nachname'] || '',
            birthDate: row['Geburtsdatum'] || '',
            email: row['Email'] || '',
            phone: row['Telefon'] || '',
            adress: row['Adresse'] || '',
            adult: (row['Erwachsen'] || '').toLowerCase() === 'ja',
            member: (row['Mitglied'] || '').toLowerCase() === 'ja',
            team: (row['Teamspieler'] || '').toLowerCase() === 'ja',
            skillLevel: row['Spielstärke'] || '',
            trainigGroup: row['Trainingsgruppe'] || '',
            sex: row['Geschlecht'] || '',
            frequence: row['Häufigkeit'] || '',
            comment: row['Kommentar'] || '',
            day: row['Zugewiesener Tag'] || '',
            hour: row['Zugewiesene Stunde'] ? parseInt(row['Zugewiesene Stunde']) : null,
            coach: row['Trainer'] || '',
            // Parse available times from day columns
            availableTimes: [
              ...parseDayHours(row['Montag'], 'Montag'),
              ...parseDayHours(row['Dienstag'], 'Dienstag'),
              ...parseDayHours(row['Mittwoch'], 'Mittwoch'),
              ...parseDayHours(row['Donnerstag'], 'Donnerstag'),
              ...parseDayHours(row['Freitag'], 'Freitag'),
              ...parseDayHours(row['Samstag'], 'Samstag')
            ].filter(t => t)
          };
        });

        console.log("Parsed students sample:", parsed[0]);

        setParsedStudents(parsed);

        // Run validation
        const validation = validateImport(parsed, students);
        setValidationResult(validation);

        // Create preview data for table display
        // Convert first 10 parsed students back to row format for preview
        const previewRows = parsed.slice(0, 10).map(student => [
          student.firstName,
          student.lastName,
          student.birthDate,
          student.email,
          student.phone,
          student.adress
        ]);

        setPreviewData({
          headers: ['Vorname', 'Nachname', 'Geburtsdatum', 'Email', 'Telefon', 'Adresse'],
          rows: previewRows,
          totalRows: parsed.length
        });
        setShowPreview(true);
      } catch (error) {
        console.error("Fehler beim Parsen der CSV:", error);
        toast.error(`Fehler beim Lesen der CSV-Datei: ${error.message}`);
      }
    };
    reader.onerror = () => {
      toast.error("Fehler beim Lesen der Datei");
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleShowValidation = () => {
    if (!validationResult) {
      toast.warning("Keine Validierungsdaten verfügbar");
      return;
    }
    setShowValidation(true);
  };

  const handleCancelImport = () => {
    setShowValidation(false);
  };

  const handleProceedImport = async () => {
    if (!csvFile) {
      toast.warning("Bitte wählen Sie eine CSV-Datei aus");
      return;
    }

    // Close validation modal
    setShowValidation(false);

    try {
      const formData = new FormData();
      formData.append('file', csvFile);

      console.log("Sending CSV import request...");
      const response = await axios.post(`${API_URL}/api/students/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log("Import response:", response.data);
      toast.success(`Import erfolgreich! ${response.data.imported} Schüler importiert.`);
      setShowPreview(false);
      setShowValidation(false);
      setCsvFile(null);
      setPreviewData(null);
      setValidationResult(null);
      setParsedStudents([]);
      fetchData(); // Reload data
    } catch (error) {
      console.error("Fehler beim Import:", error);
      console.error("Error details:", error.response?.data);
      const errorMsg = error.response?.data?.error || error.message;
      const details = error.response?.data?.details || '';
      toast.error(`Fehler beim Import: ${errorMsg}${details ? ' - ' + details : ''}`);
    }
  };

  const handleCSVExport = async () => {
    try {
      // Fetch fresh data
      const { data: allStudents } = await axios.get(`${API_URL}/api/students`);
      const { data: coaches } = await axios.get(`${API_URL}/api/coaches`);

      // Create coach lookup map
      const coachMap = new Map();
      coaches.forEach(coach => {
        coachMap.set(String(coach._id), `${coach.firstName} ${coach.lastName}`);
      });

      // Sort students: adults by skillLevel, children by trainigGroup
      const sortedStudents = [...allStudents].sort((a, b) => {
        // Adults first, then children
        if (a.adult && !b.adult) return -1;
        if (!a.adult && b.adult) return 1;

        if (a.adult && b.adult) {
          // Sort adults by skillLevel
          const skillOrder = ["Anfänger", "wenig Fortgeschritten", "Fortgeschritten", "gute:r Spieler:in"];
          const aIndex = skillOrder.indexOf(a.skillLevel || "");
          const bIndex = skillOrder.indexOf(b.skillLevel || "");
          return aIndex - bIndex;
        } else {
          // Sort children by trainigGroup
          const groupOrder = ["Kinderland", "Rot", "Orange", "Grün", "Gelb Hobby", "Gelb Team"];
          const aIndex = groupOrder.indexOf(a.trainigGroup || "");
          const bIndex = groupOrder.indexOf(b.trainigGroup || "");
          return aIndex - bIndex;
        }
      });

      // Helper to convert boolean to Ja/Nein
      const boolToString = (val) => val === true ? "Ja" : "Nein";

      // Helper to get available times by day
      const getAvailableTimesByDay = (times) => {
        const days = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];
        const timesByDay = {};

        // Initialize all days as empty
        days.forEach(day => {
          timesByDay[day] = [];
        });

        if (Array.isArray(times)) {
          times.forEach(time => {
            const [day, hour] = time.split(" ");
            if (timesByDay[day]) {
              timesByDay[day].push(hour);
            }
          });
        }

        // Sort hours for each day
        days.forEach(day => {
          timesByDay[day].sort((a, b) => a - b);
        });

        return timesByDay;
      };

      // CSV Headers
      const headers = [
        "Vorname",
        "Nachname",
        "Geburtsdatum",
        "Email",
        "Telefon",
        "Adresse",
        "Erwachsen",
        "Mitglied",
        "Teamspieler",
        "Spielstärke",
        "Trainingsgruppe",
        "Geschlecht",
        "Montag",
        "Dienstag",
        "Mittwoch",
        "Donnerstag",
        "Freitag",
        "Samstag",
        "Häufigkeit",
        "Zugewiesener Tag",
        "Zugewiesene Stunde",
        "Trainer",
        "Kommentar"
      ];

      // Build CSV rows
      const rows = sortedStudents.map(student => {
        const coachName = student.coach ? (coachMap.get(String(student.coach)) || student.coach) : "";
        const timesByDay = getAvailableTimesByDay(student.availableTimes);

        return [
          student.firstName || "",
          student.lastName || "",
          student.birthDate || "",
          student.email || "",
          student.phone || "",
          student.adress || "",
          boolToString(student.adult),
          boolToString(student.member),
          student.adult ? "" : boolToString(student.team),
          student.adult ? (student.skillLevel || "") : "",
          student.adult ? "" : (student.trainigGroup || ""),
          student.adult ? (student.sex || "") : "",
          timesByDay["Montag"].join(", ") || "",
          timesByDay["Dienstag"].join(", ") || "",
          timesByDay["Mittwoch"].join(", ") || "",
          timesByDay["Donnerstag"].join(", ") || "",
          timesByDay["Freitag"].join(", ") || "",
          timesByDay["Samstag"].join(", ") || "",
          student.frequence || "",
          student.day || "",
          student.hour !== null && student.hour !== undefined ? student.hour : "",
          coachName,
          student.comment || ""
        ];
      });

      // Escape CSV fields (handle quotes and commas)
      const escapeCSV = (field) => {
        const str = String(field);
        if (str.includes('"') || str.includes(',') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      // Build CSV content
      const csvContent = [
        headers.map(escapeCSV).join(","),
        ...rows.map(row => row.map(escapeCSV).join(","))
      ].join("\n");

      // Add BOM for proper Excel UTF-8 support
      const BOM = "\uFEFF";
      const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });

      // Generate filename with current date
      const date = new Date()
        .toISOString()
        .slice(0, 10);

      // Trigger download
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `students_${date}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(`✅ CSV export complete: ${sortedStudents.length} students exported`);
      toast.success(`${sortedStudents.length} Schüler erfolgreich exportiert`);
    } catch (error) {
      console.error("Fehler beim CSV-Export:", error);
      toast.error("Fehler beim CSV-Export");
    }
  };

  if (loading) {
    return (
      <div className="export-container">
        <h2>Lädt...</h2>
      </div>
    );
  }

  return (
    <div className="export-container">
      <h1>Import/Export</h1>

      <div className="export-section">
        <h2>📊 CSV Export</h2>
        <p>Exportiere alle Schülerdaten als CSV-Datei.</p>
        <button className="export-button csv-button" onClick={handleCSVExport}>
          CSV-Schülerdaten herunterladen
        </button>
        <div className="export-info">
          <small>
            {students.length} Schüler werden exportiert
          </small>
        </div>
      </div>

      <div className="export-section">
        <h2>📄 PDF Export</h2>
        <p>Exportiere den Kursplan als PDF mit einer Seite pro Trainer.</p>
        <button className="export-button pdf-button" onClick={handlePDFExport}>
          PDF-Kursplan herunterladen
        </button>
        <div className="export-info">
          <small>
            {students.length} Schüler • {schedule.length} Kurse
          </small>
        </div>
      </div>

      <div className="export-section">
        <h2>📥 Schüler für neuen Kursplan importieren</h2>
        <p>Importiere Schüler aus einer CSV-Datei (alle vorhandenen Schüler werden gelöscht).</p>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ marginBottom: '10px' }}
        />
        {showPreview && previewData && validationResult && (
          <div className="csv-preview">
            <h3>Vorschau ({previewData.totalRows} Schüler)</h3>

            {/* Validation Summary */}
            <div className="validation-summary-inline" style={{
              padding: '12px',
              marginBottom: '12px',
              borderRadius: '6px',
              background: validationResult.hasErrors ? '#fff5f5' : validationResult.hasWarnings || validationResult.hasDuplicates ? '#fffbf0' : '#f0fff4',
              border: `2px solid ${validationResult.hasErrors ? '#ff4444' : validationResult.hasWarnings || validationResult.hasDuplicates ? '#ffaa00' : '#00cc44'}`
            }}>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '14px' }}>
                {validationResult.hasErrors && (
                  <span style={{ color: '#cc0000', fontWeight: '600' }}>
                    🚫 {validationResult.errors.length} Fehler
                  </span>
                )}
                {validationResult.hasDuplicates && (
                  <span style={{ color: '#cc6600', fontWeight: '600' }}>
                    ⚠️ {validationResult.duplicates.length} Duplikate
                  </span>
                )}
                {validationResult.hasWarnings && (
                  <span style={{ color: '#cc6600', fontWeight: '600' }}>
                    ⚠️ {validationResult.warnings.length} Warnungen
                  </span>
                )}
                {!validationResult.hasErrors && !validationResult.hasWarnings && !validationResult.hasDuplicates && (
                  <span style={{ color: '#008822', fontWeight: '600' }}>
                    ✅ Keine Probleme gefunden
                  </span>
                )}
              </div>
            </div>

            <div style={{ overflowX: 'auto', maxHeight: '300px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr>
                    {previewData.headers.slice(0, 6).map((header, i) => (
                      <th key={i} style={{ border: '1px solid #ddd', padding: '8px', background: '#f0f0f0' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map((row, i) => (
                    <tr key={i}>
                      {row.slice(0, 6).map((cell, j) => (
                        <td key={j} style={{ border: '1px solid #ddd', padding: '8px' }}>
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="export-button import-button"
              onClick={handleShowValidation}
              style={{ marginTop: '12px' }}
            >
              📋 Validierung ansehen & importieren
            </button>
          </div>
        )}
      </div>

      <div className="export-section">
        <h2>🗑️ Leeren Kursplan starten</h2>
        <p>Löscht alle Schüler aus der Datenbank. Nur Trainer bleiben erhalten.</p>
        <button className="export-button blank-button" onClick={handleBlankSchedule}>
          Alle Schüler löschen
        </button>
        <div className="export-info">
          <small>
            ⚠️ Sie können Schüler später aus gespeicherten Plänen wiederherstellen
          </small>
        </div>
      </div>

      {/* Import Validation Modal */}
      {showValidation && validationResult && (
        <ImportValidationModal
          validation={validationResult}
          totalExisting={students.length}
          onCancel={handleCancelImport}
          onProceed={handleProceedImport}
        />
      )}
    </div>
  );
};

export default Export;
